import type { FC } from 'react';
import React, { useState } from 'react';
import { Box, ButtonV2, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { InputV2InlineLabel } from '@shared/joykit/packages/core/components/InputV2InlineLabel/InputV2InlineLabel';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog/ResponsiveDialog';
import { drawerOverrides as defaultDrawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useTranslation } from '@shared/core';
import { truncateWithinLength, useCharacterCounter } from '@shared/utils/characterCount';

type EventPersonNameDialogProps = Readonly<{
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (displayName: string) => void;
}>;

// Mounted fresh each time the dialog opens and torn down on close, so `displayName`
// resets on reopen with no isOpen effect. No auto-focus on mount: on mobile it pops the
// keyboard over the bottom-sheet input; the guest taps the field to focus when ready.
const EventPersonNameDialogForm: FC<Pick<EventPersonNameDialogProps, 'isSubmitting' | 'onSubmit'>> = ({ isSubmitting, onSubmit }) => {
  useCharacterCounter();
  const { tq } = useTranslation('eventPersonGuest');
  const [displayName, setDisplayName] = useState('');
  const trimmed = displayName.trim();
  const isValid = trimmed.length > 0;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid || isSubmitting) return;
    onSubmit(trimmed);
  };

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={6}
      width="100%"
      textAlign="center"
    >
      <TextV2
        typographyVariant="hed6"
        fontFamily="Inter UI"
        color="mono14"
        textTransform="none"
      >
        {tq`Before you 💙 that…`}
      </TextV2>
      <TextV2
        typographyVariant="body2"
        fontFamily="Inter UI"
        color="mono10"
      >
        {tq`Drop your name so the couple and other guests know who's been admiring their memories.`}
      </TextV2>
      <InputV2InlineLabel
        label={tq`Your Name`}
        value={displayName}
        // InputV2 doesn't type `maxLength`, so cap the name here.
        onChange={event => setDisplayName(truncateWithinLength(event.target.value, 80))}
        isDisabled={isSubmitting}
        autoComplete="name"
      />
      <ButtonV2
        type="submit"
        shape="rounded"
        intent="neutral"
        width="100%"
        marginTop={4}
        loading={isSubmitting}
        disabled={!isValid}
      >
        {tq`Continue`}
      </ButtonV2>
    </Box>
  );
};

/**
 * Internal to EventPersonProvider — collects a display name to create an event_person
 * session. Single-field by design. ResponsiveDialog (drawer on mobile, dialog on desktop)
 * renders the form only while open; no title is passed, so the header holds just the X.
 * `removeBodyPadding` strips the built-in top padding so the heading isn't pushed below
 * the corner X — our own override values then own the spacing.
 */
export const EventPersonNameDialog: FC<EventPersonNameDialogProps> = ({ isOpen, isSubmitting, onClose, onSubmit }) => (
  <ResponsiveDialog
    isOpen={isOpen}
    onClose={onClose}
    hideDivider
    removeBodyPadding
    dialogOverrides={{
      Content: { props: { width: ['100%', '476px'], borderRadius: pxToRem(16) } },
      Header: { props: { paddingX: [4, 6], paddingTop: [4, 6], paddingBottom: 0 } },
      Body: { props: { paddingX: [6, 9], paddingTop: 4, paddingBottom: [6, 9] } },
    }}
    drawerOverrides={{
      ...defaultDrawerOverrides,
      Body: { props: { paddingTop: 0 } },
    }}
  >
    <EventPersonNameDialogForm
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
    />
  </ResponsiveDialog>
);
